import { Dispatch, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import { CurrencyEnum, LinkedAccountStatusEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { WithdrawalRequest } from '../../../lib/models/crm/types';
import { AccountInfo } from '../../../lib/models/linked-account/types';
import { RootState } from '../../../lib/store';
import { getAccountsAvailableCash } from '../../../lib/store/common-actions';
import { getCurrentAccountTradingPlan } from '../../../lib/store/crm/helpers';
import {
  registerWithdrawalRequest,
  setBankAccountIdByLastWithdrawal,
} from '../../../lib/store/payment';
import { isActiveAccount } from '../../../lib/store/payment/helpers';
import { GetAllAvailableCashPayload } from '../../../lib/store/reporting/types';
import { fixToFloat, formatCurrency, formatCurrencyBySymbol } from '../../../lib/util/DataHelpers';

import iconAddNew from '../../../assets/img/icon-add_new.svg';
import iconBank from '../../../assets/img/icon-bank.svg';
import InputErrorIcon from '../../../assets/img/input-error-icon.svg';
import config from '../../../config';
import { useBrowserWidth, useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import CommonDropdown, { CommonDropdownOption } from '../../../shared_components/CommonDropdown/CommonDropdown';
import { UI_ROUTES } from '../../../util/routes';
import { BaseModal, ClickableDiv, MobileDrawer } from '../..';
import GoBackButton from '../../GoBackButton/GoBackButton';
import LogoAndCloseButton from '../../LogoAndCloseButton/LogoAndCloseButton';
import { useWithdrawButton } from '../../Payouts/PayoutsHeader/useWithdrawButton';

import Modal from './modal/Modal';

import styles from './WithdrawalAmount.module.scss';

const MAX_BANK_NAME_LENGTH = 28;
const MAX_SELECTED_BANK_NAME_LENGTH = 18;

const WithdrawalAmount = (props: Props) => {
  const {
    tradingAccounts,
    linkedAccounts,
    linkedAccountIdByLastWithdrawal,
    sendWithdrawalRequest,
    setBankAccountIdValueByLastWithdrawal,
    availableCash,
    payoutSplit,
    accountStatusData,
    currentPlan,
    referenceIds,
    getAvailablePayout,
  } = props;

  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const navigate = useNavigate();
  const { totalRealizedPnl } = accountStatusData;
  const isDefaultGoBackButton = browserWidthSize >= 989 && !isMobile;

  // Show all linked accounts (including pending ones)
  const convertedActiveLinkedAccounts = linkedAccounts.slice(0).reverse();

  const getPreselectedAccount = () => {
    if (linkedAccountIdByLastWithdrawal) {
      return linkedAccounts.find((account) => account.id === linkedAccountIdByLastWithdrawal);
    }
    const newest = linkedAccounts.slice().reverse();
    return (
      newest.find((a) => a.status === LinkedAccountStatusEnum.Active)
      ?? newest.find((a) => a.status === LinkedAccountStatusEnum.New)
    );
  };

  const preselectedAccount = getPreselectedAccount();
  const linkedAccountsData = preselectedAccount
    ? [preselectedAccount]
    : convertedActiveLinkedAccounts;

  const linkedAccountName = linkedAccountsData.length > 0 ? linkedAccountsData[0].account_info.bank_name : '';
  let linkedAccountIban = linkedAccountsData.length > 0 ? linkedAccountsData[0].account_info.iban : '';
  if (linkedAccountIban === null) {
    linkedAccountIban = linkedAccountsData[0].account_info.account;
  }

  const [isOpenBankAccounts, setIsOpenBankAccounts] = useState(false);
  const [openPopoverInfo, setOpenPopoverInfo] = useState(false);
  const [isShowNoActiveAccountModal, setIsShowNoActiveAccountModal] = useState(false);

  const { potentialPayout } = useWithdrawButton({
    currentPlan,
    availableCash,
    payoutSplit,
    totalRealizedPnl,
    isGettingFirstAndLastTrade: false,
    hasOlderTradeThanFourteenDays: true,
  });

  const minimumWithdrawal = (currentPlan?.daily_hard_bp ?? 0) * 0.01;

  const [dataValues, setDataValues] = useState({
    linkedAccontData: linkedAccountsData[0],
    linkedAccountName,
    linkedAccountIban,
    amountValue: '',
  });
  const calculatedCash = potentialPayout;
  const isBelowMinimum = !!dataValues.amountValue && Number(dataValues.amountValue) < fixToFloat(minimumWithdrawal);
  const isAboveMaximum = Number(dataValues.amountValue) > fixToFloat(calculatedCash);
  const hasAmountError = isBelowMinimum || isAboveMaximum;

  useEffect(() => {
    if (referenceIds?.length > 0) {
      getAvailablePayout({ accountReferenceIds: referenceIds });
    }
  }, []);

  // Check if selected account is pending
  const isSelectedAccountPending = dataValues.linkedAccontData?.status === LinkedAccountStatusEnum.New;

  const handleClickBankAccount = (linkedAccount) => {
    if (!linkedAccount) {
      setIsOpenBankAccounts(false);
      return;
    }

    setDataValues({
      ...dataValues,
      linkedAccontData: linkedAccount,
      linkedAccountName: linkedAccount.account_info.bank_name,
      linkedAccountIban: linkedAccount.account_info.iban || linkedAccount.account_info.account,
    });
    setIsOpenBankAccounts(false);
  };

  const truncate = (text: string, maxLength: number) =>
    (text.length > maxLength ? `${text.slice(0, maxLength)}...` : text);

  const bankAccountOptions = convertedActiveLinkedAccounts.map((account, index) => ({
    id: String(account.id),
    label: truncate(account.account_info.bank_name, MAX_BANK_NAME_LENGTH),
    subText: account.account_info.iban || account.account_info.account,
    icon: iconBank,
    sectionLabel: !isMobile && index === 0 ? lang.payoutsBankTransfer() : undefined,
    badge: account.status === LinkedAccountStatusEnum.New
      ? lang.commonVerificationStepStatusPending()
      : undefined,
    badgeClassName: styles.pendingBadge,
  }));

  const selectedBankName = dataValues.linkedAccountName || linkedAccountName;
  const selectedBankIban = dataValues.linkedAccountIban || linkedAccountIban;
  const selectedBankAccount = {
    id: String(dataValues.linkedAccontData?.id ?? ''),
    label: `${truncate(selectedBankName, MAX_SELECTED_BANK_NAME_LENGTH)}${
      selectedBankIban ? ` \u2022 ${String(selectedBankIban).slice(-4)}` : ''}`,
  };

  const handleSelectBankAccount = (option: CommonDropdownOption) => {
    handleClickBankAccount(convertedActiveLinkedAccounts.find(
      (account) => String(account.id) === option.id,
    ));
  };

  const handleAddBankAccount = () => {
    navigate(UI_ROUTES.payouts + UI_ROUTES.addYourBankAccount, {
      state: {
        previousPage: UI_ROUTES.payouts + UI_ROUTES.withdrawalAmount,
      },
    });
  };

  const bankAccountsFooter = (
    <ClickableDiv className={styles.addBankAccount} onClick={handleAddBankAccount}>
      <img src={iconAddNew} alt="icon small plus" />
      <span className={styles.addBankAccountText}>{lang.mobileFundsScreenAddBankAccModalBtn()}</span>
    </ClickableDiv>
  );

  const handleRequestWithdrawal = () => {
    const activeTradingAccount = tradingAccounts.find(isActiveAccount);
    if (!activeTradingAccount) {
      setIsShowNoActiveAccountModal(true);
      return;
    }
    setBankAccountIdValueByLastWithdrawal(dataValues.linkedAccontData?.id);
    sendWithdrawalRequest({
      amount: Number(dataValues.amountValue),
      bank_details: dataValues.linkedAccontData.account_info as AccountInfo,
      currency_code: CurrencyEnum.USD,
    });
    navigate(UI_ROUTES.payouts, { state: { showWithdrawalModal: true } });
  };

  return (
    <div className={styles.wrapperContainer}>
      <LogoAndCloseButton
        displayCloseButton={!isMobile}
        displayBackButton={isDefaultGoBackButton}
        onBackBtnPress={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
        onClose={() => navigate(UI_ROUTES.payouts)}
        newWebGoBackButtonDesign
      />
      {isMobile && (
        <div className={styles.mobileBackContainer}>
          <GoBackButton
            onBackBtnPress={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
          />
        </div>
      )}
      <div className={styles.containerContent}>
        {isMobile ? (
          <>
            <div className={styles.wrapperContentMobile}>
              <h3 className={styles.title}>{lang.mobileWithdrawScreenTitle()}</h3>
              <span className={styles.description}>{lang.mobileWithdrawScreenSubtitle()}</span>
              <div className={styles.mobileFormGroup}>
                <div className={styles.bankTextMobile}>
                  {lang.mobileWithdrawScreenBankAccount()}
                </div>
                <CommonDropdown
                  options={bankAccountOptions}
                  selectedOption={selectedBankAccount}
                  onSelectOption={handleSelectBankAccount}
                  isOpen={isOpenBankAccounts}
                  setIsOpen={setIsOpenBankAccounts}
                  title={lang.payoutsBankTransfer()}
                  iconClassName={styles.dropdownIcon}
                  footer={bankAccountsFooter}
                />
                <div className={styles.currencyAmountWrapper}>
                  <div className={styles.amountWrapper}>
                    <div className={styles.amountTextMobile}>{lang.mobileDepositAmountLabel()}</div>
                    <div
                      className={classNames(styles.currencyInputWrapper, {
                        [styles.hasError]: hasAmountError,
                      })}
                    >
                      <CurrencyInput
                        value={dataValues.amountValue}
                        onValueChange={(value) =>
                          setDataValues({ ...dataValues, amountValue: value as string })}
                        className={classNames(styles.input, {
                          [styles.errorInput]: hasAmountError,
                        })}
                        placeholder={'$0.00'}
                        allowNegativeValue={false}
                        prefix={'$'}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.availableCashErrorWrapper}>
                  {hasAmountError && (
                    <img src={InputErrorIcon} alt="Error icon" className={styles.errorIcon} />
                  )}
                  <span
                    className={classNames(styles.textAvailableCash, {
                      [styles.textAvailableCashError]: hasAmountError,
                    })}
                  >
                    {isBelowMinimum
                      ? lang.minimumBankAmount(formatCurrencyBySymbol(minimumWithdrawal, '$'))
                      : `${lang.availableCash()}: ${formatCurrencyBySymbol(calculatedCash, '$')}`}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.confirmAndFeeMobileWrapper}>
              <Button
                className={classNames(styles.buttonRequest, {
                  [styles.buttonRequestDisabled]:
                    !dataValues.amountValue
                    || Number(dataValues.amountValue) === 0
                    || hasAmountError
                    || isOpenBankAccounts
                    || isSelectedAccountPending,
                })}
                fullWidth
                disabled={
                  !dataValues.amountValue
                  || Number(dataValues.amountValue) === 0
                  || hasAmountError
                  || isOpenBankAccounts
                  || isSelectedAccountPending
                }
                onClick={handleRequestWithdrawal}
              >
                {lang.mobileWithdrawScreenRequestBtn()}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.wrapperContent}>
              <h3 className={styles.title}>{lang.mobileWithdrawScreenTitle()}</h3>
              <span className={styles.description}>{lang.mobileWithdrawScreenSubtitle()}</span>
              <div>
                <div className={styles.amountText}>{lang.mobileWithdrawScreenBankAccount()}</div>
                <CommonDropdown
                  options={bankAccountOptions}
                  selectedOption={selectedBankAccount}
                  onSelectOption={handleSelectBankAccount}
                  isOpen={isOpenBankAccounts}
                  setIsOpen={setIsOpenBankAccounts}
                  title={lang.payoutsBankTransfer()}
                  iconClassName={styles.dropdownIcon}
                  footer={bankAccountsFooter}
                />
              </div>
              <div className={styles.wrapperFieldAmount}>
                <div className={styles.currencyAmountWrapper}>
                  <div className={styles.amountWrapper}>
                    <div className={styles.amountText}>{lang.mobileDepositAmountLabel()}</div>
                    <div
                      className={classNames(styles.currencyInputWrapper, {
                        [styles.hasError]: hasAmountError,
                      })}
                    >
                      <CurrencyInput
                        value={dataValues.amountValue}
                        onValueChange={(value) =>
                          setDataValues({ ...dataValues, amountValue: value as string })}
                        className={classNames(styles.input, {
                          [styles.errorInput]: hasAmountError,
                        })}
                        placeholder={'$0.00'}
                        allowNegativeValue={false}
                        prefix={'$'}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.availableCashErrorWrapper}>
                  {hasAmountError && (
                    <img src={InputErrorIcon} alt="Error icon" className={styles.errorIcon} />
                  )}
                  <span
                    className={classNames(styles.textAvailableCash, {
                      [styles.textAvailableCashError]: hasAmountError,
                    })}
                  >
                    {isBelowMinimum
                      ? lang.minimumBankAmount(formatCurrencyBySymbol(minimumWithdrawal, '$'))
                      : `${lang.availableCash()}: ${formatCurrencyBySymbol(calculatedCash, '$')}`}
                  </span>
                </div>
              </div>
              <Button
                className={classNames(styles.buttonRequest, {
                  [styles.buttonRequestDisabled]:
                    !dataValues.amountValue
                    || Number(dataValues.amountValue) === 0
                    || hasAmountError
                    || isOpenBankAccounts
                    || isSelectedAccountPending,
                })}
                fullWidth
                disabled={
                  !dataValues.amountValue
                  || Number(dataValues.amountValue) === 0
                  || hasAmountError
                  || isOpenBankAccounts
                  || isSelectedAccountPending
                }
                onClick={handleRequestWithdrawal}
              >
                {lang.mobileWithdrawScreenRequestBtn()}
              </Button>
            </div>
          </>
        )}
      </div>
      <BaseModal isOpen={Boolean(openPopoverInfo)}>
        <p className={styles.messageAvailableCashTitle}>
          {lang.mobileWithdrawScreenInfoModalTitle(formatCurrency(calculatedCash))}
        </p>
        <p className={styles.messageAvailableCash}>
          {lang.mobileWithdrawScreenInfoModalDescription()}
        </p>
        <Button fullWidth onClick={() => setOpenPopoverInfo(false)}>
          {lang.commonContinueButton()}
        </Button>
      </BaseModal>

      {isMobile ? (
        <MobileDrawer
          title={lang.walletWithdrawalsRestricted()}
          isOpen={isShowNoActiveAccountModal}
          onClose={() => setIsShowNoActiveAccountModal(false)}
        >
          <div className={styles.drawerText}>
            <p>
              {lang.walletNoActiveAccountPleaseContact()}
              {' '}
              <a
                className={styles.supportLink}
                href={config.links.helpCenterUrl}
                target="_blank"
                rel="noreferrer"
              >
                {lang.commonSupport()}
              </a>
              {'.'}
            </p>
          </div>
          <Button
            className={styles.okBtn}
            fullWidth
            onClick={() => setIsShowNoActiveAccountModal(false)}
          >
            {lang.commonOkay()}
          </Button>
        </MobileDrawer>
      ) : (
        <BaseModal isOpen={isShowNoActiveAccountModal}>
          <Modal
            title={lang.walletWithdrawalsRestricted()}
            descriptionComponent={(
              <div>
                {lang.walletNoActiveAccountPleaseContact()}
                {' '}
                <a href={config.links.helpCenterUrl} target="_blank" rel="noreferrer">
                  {lang.commonSupport()}
                </a>
              </div>
            )}
          />
        </BaseModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingAccounts: state.payment.tradingAccounts,
  linkedAccounts: state.payment.linkedAccounts,
  linkedAccountIdByLastWithdrawal: state.payment.linkedAccountIdByLastWithdrawal,
  availableCash: state.payment.availableCashInfo.availableCash.map(
    (available) => available.totalInBaseCurrency.availableCash,
  ),
  payoutSplit: getCurrentAccountTradingPlan(state.crm)?.payout_split,
  accountStatusData: state.closeout.accountStatusData,
  currentPlan: getCurrentAccountTradingPlan(state.crm),
  referenceIds: state.crm.cashAccounts,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  sendWithdrawalRequest: (data: WithdrawalRequest) => dispatch(registerWithdrawalRequest(data)),
  setBankAccountIdValueByLastWithdrawal: (id: number) =>
    dispatch(setBankAccountIdByLastWithdrawal(id)),
  getAvailablePayout: (payload: GetAllAvailableCashPayload) =>
    dispatch(getAccountsAvailableCash(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector>;

export default connector(WithdrawalAmount);
